import { useState, useEffect, useRef } from 'react';
import Decorator from '@evertrue/client-decorator';
import Api from 'entities/helpers/api';

/**
 * Searches contacts via the contacts search API with 300ms debouncing.
 * Used by cadence, prospect plan, and bulk message creation pages for recipient selection.
 *
 * @returns {{ query: string, setQuery: Function, results: Array, loading: boolean }}
 */
const useContactSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  // Tracks which request is current; incremented on each dispatch so stale
  // responses from rapid typing are silently discarded.
  const reqCounterRef = useRef(0);

  useEffect(() => {
    if (!query || query.length < 2) {
      // Invalidate any in-flight request and clear pending debounce so stale
      // results don't repopulate the list after the user clears the input.
      ++reqCounterRef.current;
      if (timerRef.current) clearTimeout(timerRef.current);
      setResults([]);
      setLoading(false);
      return;
    }

    // Invalidate any in-flight request immediately so a response that arrives
    // during the debounce window is also discarded (not just post-dispatch).
    const reqId = ++reqCounterRef.current;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setLoading(true);
      // Split on whitespace, filter empty tokens to avoid bare `*` wildcards.
      const names = query.trim().split(/\s+/).filter(Boolean);
      const must = names.map((name) => ({
        name_first: { wildcard: `${name}*` },
        name_last: { wildcard: `${name}*` },
        name_nick: { wildcard: `${name}*` },
        'emails.email': { wildcard: `${name}*` },
      }));

      Api.CONTACTS.SEARCH.post({
        params: { limit: 20 },
        data: JSON.stringify({ must, must_not: [{ contact_type: { match: 'User' } }] }),
      })
        .then((resp) => {
          if (reqId !== reqCounterRef.current) return;
          const rawItems = resp?.items || [];
          setResults(rawItems.map(Decorator.Contacts.parse));
          setLoading(false);
        })
        .catch(() => {
          if (reqId !== reqCounterRef.current) return;
          setResults([]);
          setLoading(false);
        });
    }, 300);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query]);

  return { query, setQuery, results, loading };
};

export default useContactSearch;
